import React, { useState, useEffect } from "react";
import "./layout.css";
import "./Sidebar.css";
import appConfig from "../../constants/config";
import { postApiAxios } from "../../utils/apiClient";
import { Link } from "react-router-dom";
import { useAuth } from "../../components/AuthProvider/useAuth";
import { translate } from "../../utils/translateUtils";

interface MenuProps {
  menuItems?: MenuItem[];
  parentModuleId?: string | null;
  style?: React.CSSProperties;
  imgStyle?: React.CSSProperties;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setToggleSideBar?: any;
}

interface MenuItem {
  parent_module_id?: number | null;
  module_id: number;
  url: string;
  tooltip_message: string;
  module_name: string;
  icon: string;
  sequence: number;
  smallDescription?: string;
}

const SideBar: React.FC<MenuProps> = ({
  parentModuleId,
  style = {},
  imgStyle = {},
  setLoading,
  setToggleSideBar,
}) => {
  const [tableData, setTableData] = useState<MenuItem[]>([]);
  const [openMenu, setOpenMenu] = useState<number | null>(null);
  const { currentUser, isImpersonated } = useAuth();

  //Temporary variable that don't want to show redundant sidebar
  let exceptedSidebarModuleIds: number[] = [];

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        const param: { employee_email: string, employee_profile: any, isImpersonated: any } = {
          employee_email: currentUser
            ? currentUser.work_email_id
            : appConfig.connectionString.DebugUserName,
          employee_profile: currentUser ? currentUser : null,
          isImpersonated: isImpersonated ? 1 : 0
        };
        let menuItems = await postApiAxios(
          appConfig.connectionString.ws + "/api/getMenu",
          param
        );

        let customSidebars: MenuItem[] = [];

        // if (currentUser?.my_team_access === 1 && !isImpersonated) {
        //   const myTeam: MenuItem = {
        //     module_id: 12,
        //     module_name: `My Team`,
        //     parent_module_id: null,
        //     url: "../ExpReport/ExpReport?mode=team",
        //     icon: "../Images/08_Team-expenses-report.png",
        //     tooltip_message: "My Team",
        //     sequence: 12,
        //     smallDescription: currentUser?.total_my_team_pending?.toString(),
        //   };
        //   customSidebars.push(myTeam);
        // }

        if (isImpersonated) {
          menuItems = menuItems.filter((item: any) => item.module_id !== 13);
        }

        setTableData([...menuItems, ...customSidebars]);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData().then((r) => {
      setLoading(false);
    });
  }, [currentUser, isImpersonated, setLoading]);

  // Helper: Get children for a parent module_id
  const getChildren = (parentId: number) =>
    tableData.filter((item) => item.parent_module_id === parentId);

  // Recursive menu item renderer
  const menuItem = (item: MenuItem) => {
    const children = getChildren(item.module_id);
    const hasChildren = children.length > 0;
    const isOpen = openMenu === item.module_id;

    const handleClick = (e: React.MouseEvent) => {
      if (hasChildren) {
        e.preventDefault();
        setOpenMenu(isOpen ? null : item.module_id);
      } else if (setToggleSideBar) {
        setToggleSideBar();
      }
    };

    return (
      <li
        key={item.module_id}
        style={{ textAlign: "center" }}
        id={`module-id-${item.module_id}`}
        className="side-bar-menu"
      >
        <div
          style={{ display: "flex", alignItems: "center", cursor: hasChildren ? "pointer" : "default" }}
          onClick={handleClick}
        >
          <Link
            to={item.url.replace("..", "")}
            title={item.tooltip_message}
            data-tag={item.module_name}
            tabIndex={-1}
           style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              pointerEvents: hasChildren ? "none" : "auto",
              textDecoration: "none", // no underline
              color: "white",         // white text
            }}
          >
            <img
              style={{
                height: "28px",
                width: "28px",
                verticalAlign: "bottom",
                ...imgStyle,
                ...style,
              }}
              src={`${appConfig.connectionString.appBaseName}${item.icon.replace("..", "").replace("/Images/", "/images/")}`}
              alt={item.module_name}
            />
            <span
              style={{
                marginLeft: "10px",
                verticalAlign: "bottom",
                fontSize: "16px",
              }}
              data-tag={item.module_name}
            >
              {translate(item.module_name)}{" "}
              {(item.smallDescription && item.smallDescription != "0") ? (
                <span
                  className="badge badge-light fw-bold"
                  style={{ color: "black", fontSize: 12 }}
                >
                  {item.smallDescription}
                </span>
              ) : null}
            </span>
          </Link>
        </div>
        {hasChildren && isOpen && (
          <div className="list-unstyled" style={{ paddingLeft: 20}}>
            {children
              .sort((a, b) => a.sequence - b.sequence)
              .map((child) => menuItem(child))}
          </div>
        )}
      </li>
    );
  };

  return (
    <ul className="list-unstyled components">
      {tableData
        .filter(
          (item) =>
            item.parent_module_id === parentModuleId &&
            exceptedSidebarModuleIds.indexOf(item.module_id) < 0
        )
        .sort((a, b) => a.sequence - b.sequence)
        .map((item) => menuItem(item))}
    </ul>
  );
};

export default SideBar;